import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { 
  FiSearch, 
  FiPlus, 
  FiEdit, 
  FiTrash2, 
  FiEye, 
  FiEyeOff,
  FiArrowUp,
  FiArrowDown,
  FiCalendar,
  FiCheck,
  FiX,
  FiStar,
  FiMapPin,
  FiNavigation,
  FiHome,
  FiShoppingBag,
  FiCoffee,
  FiHeart,
  FiBook,
  FiFilm,
  FiDollarSign,
  FiShoppingCart,
  FiUmbrella,
  FiBriefcase,
  FiTruck,
  FiMail,
  FiPhone,
  FiGlobe,
  FiCreditCard,
  FiCloud,
  FiZap,
  FiSun,
  FiMoon
} from 'react-icons/fi';
import { 
  FaRegCheckCircle,
  FaRegTimesCircle,
  FaHospital,
  FaSchool,
  FaUniversity,
  FaChurch,
  FaMosque,
  FaTrain,
  FaBus,
  FaTaxi,
  FaPlane,
  FaShip,
  FaCar,
  FaBicycle,
  FaWalking,
  FaTree,
  FaMountain,
  FaWater,
  FaFire,
  FaGasPump,
  FaWifi,
  FaParking,
  FaShoppingCart,
  FaUtensils
} from 'react-icons/fa';
import { 
  GiBank,
  GiPoliceBadge,
  GiFireStation,
  GiMedicalPack,
  GiPharmacy,
  GiGymBag,
  GiTennisCourt,
  GiSwimmingPool,
  GiGolfFlag,
  GiOfficeChair,
  GiFactory,
  GiFarmTractor,
  GiMarketStall,
  GiFruitTree,
  GiFlowerPot,
  GiAnimalSkull,
  GiBirdHouse,
  GiFish,
  GiCastle,
  GiMonument
} from 'react-icons/gi';
import { 
  MdLocalHospital,
  MdSchool,
  MdLocalLibrary,
  MdLocalMovies,
  MdLocalPlay,
  MdLocalParking,
  MdLocalGasStation,
  MdLocalConvenienceStore,
  MdLocalCafe,
  MdLocalRestaurant,
  MdLocalBar,
  MdLocalHotel,
  MdLocalAirport,
  MdLocalAtm,
  MdLocalPostOffice,
  MdLocalPolice,
  MdLocalFireDepartment,
  MdLocalFlorist,
  MdLocalGroceryStore,
  MdLocalLaundryService,
  MdLocalPharmacy
} from 'react-icons/md';

const NearbyLocationsManagement = () => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [viewingLocation, setViewingLocation] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortConfig, setSortConfig] = useState({ key: 'cr_date', direction: 'desc' });
  const [processing, setProcessing] = useState(false);

  const [formData, setFormData] = useState({
    nearby_location_name: '',
    status: 'active'
  });

  const API_BASE = 'https://mithahomesfrontend.pemixcels.com/api/nearbylocation';

  // Statistics
  const stats = {
    total: locations.length,
    active: locations.filter(l => l.status === 'active').length,
    inactive: locations.filter(l => l.status === 'inactive').length,
    recent: locations.filter(l => {
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      return new Date(l.cr_date) > weekAgo;
    }).length,
  };

  // Fetch all nearby locations
  const fetchLocations = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE}/all`);
      if (response.data.success) {
        setLocations(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching nearby locations:', error);
      toast.error('❌ Error loading nearby locations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLocations();
  }, []);

  // Filter locations
  const filteredLocations = locations.filter(location => {
    const matchesSearch = location.nearby_location_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         location.nid?.toString().includes(searchTerm);
    const matchesStatus = filterStatus === 'all' || location.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  // Sort locations
  const sortedLocations = React.useMemo(() => {
    if (!sortConfig.key) return filteredLocations;
    
    return [...filteredLocations].sort((a, b) => {
      // Handle date sorting
      if (sortConfig.key === 'cr_date' || sortConfig.key === 'update_time') {
        const dateA = new Date(a[sortConfig.key]);
        const dateB = new Date(b[sortConfig.key]);
        return sortConfig.direction === 'asc' ? dateA - dateB : dateB - dateA;
      }
      
      // Handle numeric sorting
      if (sortConfig.key === 'nid') {
        return sortConfig.direction === 'asc' ? a.nid - b.nid : b.nid - a.nid;
      }
      
      // Handle string sorting
      if (a[sortConfig.key] < b[sortConfig.key]) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (a[sortConfig.key] > b[sortConfig.key]) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [filteredLocations, sortConfig]);

  const handleSort = (key) => {
    setSortConfig({
      key,
      direction: sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc'
    });
  };

  // Form handlers
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      nearby_location_name: '',
      status: 'active'
    });
    setEditingLocation(null);
  };

  // Validate form before submission
  const validateForm = () => {
    if (!formData.nearby_location_name.trim()) {
      toast.error('❌ Please enter a nearby location name');
      return false;
    }
    if (formData.nearby_location_name.trim().length < 2) {
      toast.error('❌ Nearby location name must be at least 2 characters long');
      return false;
    }
    if (formData.nearby_location_name.trim().length > 200) {
      toast.error('❌ Nearby location name must be less than 200 characters');
      return false;
    }
    return true;
  };

  // ✅ Add new location or update existing
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    try {
      setProcessing(true);
      
      let response;
      if (editingLocation) {
        // Update location
        response = await axios.put(`${API_BASE}/all/${editingLocation.nid}`, formData);
      } else {
        // Create new location
        response = await axios.post(`${API_BASE}/add`, formData);
      }

      if (response.data.success) {
        toast.success(`✅ ${editingLocation ? 'Nearby location updated' : 'Nearby location added'} successfully!`);
        setShowModal(false);
        resetForm();
        fetchLocations();
      } else {
        throw new Error(response.data.message || 'Operation failed');
      }

    } catch (error) {
      console.error('Error saving nearby location:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Error saving nearby location';
      if (error.response?.status === 409) {
        toast.error('❌ Nearby location name already exists');
      } else {
        toast.error(`❌ ${errorMessage}`);
      }
    } finally {
      setProcessing(false);
    }
  };

  // ✅ Edit location
  const handleEdit = (location) => {
    setEditingLocation(location);
    setFormData({
      nearby_location_name: location.nearby_location_name || '',
      status: location.status || 'active'
    });
    setShowModal(true);
  };

  // View location details
  const handleView = (location) => {
    setViewingLocation(location);
    setShowViewModal(true);
  };

  // ✅ Toggle status
  const toggleStatus = async (id, currentStatus) => {
    const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
    try {
      const response = await axios.put(`${API_BASE}/all/${id}`, {
        status: newStatus
      });
      
      if (response.data.success) {
        toast.success(`📝 Nearby location ${newStatus === 'active' ? 'activated' : 'deactivated'}!`);
        fetchLocations();
      } else {
        throw new Error(response.data.message || 'Update failed');
      }
    } catch (error) {
      console.error('Error updating status:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Error updating status';
      toast.error(`❌ ${errorMessage}`);
    }
  };

  // ✅ Delete location
  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this nearby location? This action cannot be undone.')) {
      try {
        const response = await axios.delete(`${API_BASE}/del/${id}`);
        
        if (response.data.success) {
          toast.success('🗑️ Nearby location deleted successfully!');
          fetchLocations();
        } else {
          throw new Error(response.data.message || 'Delete failed');
        }
      } catch (error) {
        console.error('Error deleting nearby location:', error);
        const errorMessage = error.response?.data?.message || error.message || 'Error deleting nearby location';
        toast.error(`❌ ${errorMessage}`);
      }
    }
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Format time
  const formatTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get status color and icon
  const getStatusConfig = (status) => {
    if (status === 'active') {
      return {
        background: '#d1fae5',
        color: '#065f46',
        border: '#a7f3d0',
        icon: <FaRegCheckCircle />,
        text: 'Active'
      };
    } else {
      return {
        background: '#fee2e2',
        color: '#dc2626',
        border: '#fecaca',
        icon: <FaRegTimesCircle />,
        text: 'Inactive'
      };
    }
  };

  // Get location icon based on name
  const getLocationIcon = (locationName) => {
    const name = locationName.toLowerCase();
    
    // Medical facilities
    if (name.includes('hospital') || name.includes('clinic') || name.includes('medical')) {
      return <MdLocalHospital style={{ color: '#ef4444', fontSize: '1.2rem' }} />;
    } else if (name.includes('pharmacy') || name.includes('chemist')) {
      return <MdLocalPharmacy style={{ color: '#3b82f6', fontSize: '1.2rem' }} />;
    } else if (name.includes('eye') || name.includes('dental')) {
      return <FaHospital style={{ color: '#dc2626', fontSize: '1.2rem' }} />;
    
    // Educational institutions
    } else if (name.includes('school') || name.includes('college')) {
      return <MdSchool style={{ color: '#8b5cf6', fontSize: '1.2rem' }} />;
    } else if (name.includes('university')) {
      return <FaUniversity style={{ color: '#7c3aed', fontSize: '1.2rem' }} />;
    } else if (name.includes('library')) {
      return <MdLocalLibrary style={{ color: '#0ea5e9', fontSize: '1.2rem' }} />;
    
    // Transportation
    } else if (name.includes('station') || name.includes('railway')) {
      return <FaTrain style={{ color: '#f59e0b', fontSize: '1.2rem' }} />;
    } else if (name.includes('bus') || name.includes('stand')) {
      return <FaBus style={{ color: '#10b981', fontSize: '1.2rem' }} />;
    } else if (name.includes('taxi')) {
      return <FaTaxi style={{ color: '#f59e0b', fontSize: '1.2rem' }} />;
    } else if (name.includes('airport')) {
      return <MdLocalAirport style={{ color: '#6366f1', fontSize: '1.2rem' }} />;
    } else if (name.includes('parking')) {
      return <MdLocalParking style={{ color: '#64748b', fontSize: '1.2rem' }} />;
    
    // Shopping & Retail
    } else if (name.includes('mall') || name.includes('shopping') || name.includes('market')) {
      return <MdLocalGroceryStore style={{ color: '#ec4899', fontSize: '1.2rem' }} />;
    } else if (name.includes('store') || name.includes('shop')) {
      return <FaShoppingCart style={{ color: '#ef4444', fontSize: '1.2rem' }} />;
    } else if (name.includes('supermarket') || name.includes('grocery')) {
      return <MdLocalConvenienceStore style={{ color: '#84cc16', fontSize: '1.2rem' }} />;
    
    // Food & Dining
    } else if (name.includes('cafe') || name.includes('coffee')) {
      return <MdLocalCafe style={{ color: '#92400e', fontSize: '1.2rem' }} />;
    } else if (name.includes('bar') || name.includes('pub')) {
      return <MdLocalBar style={{ color: '#7c3aed', fontSize: '1.2rem' }} />;
    
    // Financial
    } else if (name.includes('bank') || name.includes('atm')) {
      return <GiBank style={{ color: '#10b981', fontSize: '1.2rem' }} />;
    
    // Government & Services
    } else if (name.includes('police')) {
      return <MdLocalPolice style={{ color: '#3b82f6', fontSize: '1.2rem' }} />;
    } else if (name.includes('fire') || name.includes('emergency')) {
      return <MdLocalFireDepartment style={{ color: '#ef4444', fontSize: '1.2rem' }} />;
    } else if (name.includes('post') || name.includes('mail')) {
      return <MdLocalPostOffice style={{ color: '#f59e0b', fontSize: '1.2rem' }} />;
    
    // Religious places
    } else if (name.includes('temple') || name.includes('church') || name.includes('mosque')) {
      return <FaChurch style={{ color: '#8b5cf6', fontSize: '1.2rem' }} />;
    
    // Recreational
    } else if (name.includes('park') || name.includes('garden')) {
      return <FaTree style={{ color: '#10b981', fontSize: '1.2rem' }} />;
    } else if (name.includes('cinema') || name.includes('theater') || name.includes('movie')) {
      return <MdLocalMovies style={{ color: '#ec4899', fontSize: '1.2rem' }} />;
    } else if (name.includes('gym') || name.includes('fitness')) {
      return <GiGymBag style={{ color: '#ef4444', fontSize: '1.2rem' }} />;

    // Business & Offices
    } else if (name.includes('office') || name.includes('business')) {
      return <GiOfficeChair style={{ color: '#64748b', fontSize: '1.2rem' }} />;
    
    // Residential
    } else if (name.includes('apartment') || name.includes('building') || name.includes('society')) {
      return <FiHome style={{ color: '#10b981', fontSize: '1.2rem' }} />;
    
    // General
    } else if (name.includes('behind') || name.includes('near') || name.includes('opposite')) {
      return <FiMapPin style={{ color: '#ef4444', fontSize: '1.2rem' }} />;
    } else if (name.includes('center') || name.includes('centre')) {
      return <FiNavigation style={{ color: '#3b82f6', fontSize: '1.2rem' }} />;
    } else if (name.includes('gas') || name.includes('petrol')) {
      return <MdLocalGasStation style={{ color: '#f59e0b', fontSize: '1.2rem' }} />;
    } else if (name.includes('laundry') || name.includes('clean')) {
      return <MdLocalLaundryService style={{ color: '#0ea5e9', fontSize: '1.2rem' }} />;
    } else if (name.includes('beauty') || name.includes('salon') || name.includes('spa')) {
      return <FiHeart style={{ color: '#ec4899', fontSize: '1.2rem' }} />;
    } else {
      return <FiMapPin style={{ color: '#6b7280', fontSize: '1.2rem' }} />;
    }
  };

  return (
    <div style={{
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '20px',
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
      background: '#f8fafc',
      minHeight: '100vh'
    }}>
      <ToastContainer 
        position="top-right" 
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />

      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: '30px',
        flexWrap: 'wrap',
        gap: '20px'
      }}>
        <div>
          <h1 style={{
            fontSize: '2.5rem',
            fontWeight: '700',
            color: '#1e293b',
            margin: '0 0 8px 0'
          }}>
            Nearby Locations Management
          </h1>
          <p style={{
            fontSize: '1.1rem',
            color: '#64748b',
            margin: '0'
          }}>
            Manage nearby locations and landmarks for properties
          </p>
        </div>
        <button 
          onClick={() => setShowModal(true)}
          style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            border: 'none',
            padding: '12px 24px',
            borderRadius: '12px',
            fontSize: '1rem',
            fontWeight: '600',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)',
            transition: 'all 0.3s ease'
          }}
          onMouseOver={(e) => {
            e.target.style.transform = 'translateY(-2px)';
            e.target.style.boxShadow = '0 8px 25px rgba(102, 126, 234, 0.4)';
          }}
          onMouseOut={(e) => {
            e.target.style.transform = 'translateY(0)';
            e.target.style.boxShadow = '0 4px 15px rgba(102, 126, 234, 0.3)';
          }}
        >
          <FiPlus style={{ fontSize: '1.1rem' }} />
          Add New Location
        </button>
      </div>

      {/* Statistics Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
        gap: '20px',
        marginBottom: '30px'
      }}>
        {/* Total Locations */}
        <div style={{
          background: 'white',
          padding: '25px',
          borderRadius: '16px',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
          display: 'flex',
          alignItems: 'center',
          gap: '20px',
          transition: 'transform 0.3s ease, box-shadow 0.3s ease'
        }}
        onMouseOver={(e) => {
          e.target.style.transform = 'translateY(-4px)';
          e.target.style.boxShadow = '0 12px 25px rgba(0, 0, 0, 0.15)';
        }}
        onMouseOut={(e) => {
          e.target.style.transform = 'translateY(0)';
          e.target.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)';
        }}
        >
          <div style={{
            width: '60px',
            height: '60px',
            borderRadius: '12px',
            background: 'linear-gradient(135deg, #667eea, #764ba2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '1.5rem',
            color: 'white'
          }}>
            <FiMapPin />
          </div>
          <div>
            <h3 style={{
              fontSize: '2rem',
              fontWeight: '700',
              color: '#1e293b',
              margin: '0 0 4px 0'
            }}>
              {stats.total}
            </h3>
            <p style={{
              fontSize: '0.95rem',
              color: '#64748b',
              margin: '0'
            }}>
              Total Locations
            </p>
          </div>
        </div>

        {/* Active Locations */}
        <div style={{
          background: 'white',
          padding: '25px',
          borderRadius: '16px',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
          display: 'flex',
          alignItems: 'center',
          gap: '20px',
          transition: 'transform 0.3s ease, box-shadow 0.3s ease'
        }}
        onMouseOver={(e) => {
          e.target.style.transform = 'translateY(-4px)';
          e.target.style.boxShadow = '0 12px 25px rgba(0, 0, 0, 0.15)';
        }}
        onMouseOut={(e) => {
          e.target.style.transform = 'translateY(0)';
          e.target.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)';
        }}
        >
          <div style={{
            width: '60px',
            height: '60px',
            borderRadius: '12px',
            background: 'linear-gradient(135deg, #10b981, #059669)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '1.5rem',
            color: 'white'
          }}>
            <FiCheck />
          </div>
          <div>
            <h3 style={{
              fontSize: '2rem',
              fontWeight: '700',
              color: '#1e293b',
              margin: '0 0 4px 0'
            }}>
              {stats.active}
            </h3>
            <p style={{
              fontSize: '0.95rem',
              color: '#64748b',
              margin: '0'
            }}>
              Active Locations
            </p>
          </div>
        </div>

        {/* Inactive Locations */}
        <div style={{
          background: 'white',
          padding: '25px',
          borderRadius: '16px',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
          display: 'flex',
          alignItems: 'center',
          gap: '20px',
          transition: 'transform 0.3s ease, box-shadow 0.3s ease'
        }}
        onMouseOver={(e) => {
          e.target.style.transform = 'translateY(-4px)';
          e.target.style.boxShadow = '0 12px 25px rgba(0, 0, 0, 0.15)';
        }}
        onMouseOut={(e) => {
          e.target.style.transform = 'translateY(0)';
          e.target.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)';
        }}
        >
          <div style={{
            width: '60px',
            height: '60px',
            borderRadius: '12px',
            background: 'linear-gradient(135deg, #ef4444, #dc2626)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '1.5rem',
            color: 'white'
          }}>
            <FiX />
          </div>
          <div>
            <h3 style={{
              fontSize: '2rem',
              fontWeight: '700',
              color: '#1e293b',
              margin: '0 0 4px 0'
            }}>
              {stats.inactive}
            </h3>
            <p style={{
              fontSize: '0.95rem',
              color: '#64748b',
              margin: '0'
            }}>
              Inactive Locations
            </p>
          </div>
        </div>

        {/* Recent Locations (Last 7 days) */}
        <div style={{
          background: 'white',
          padding: '25px',
          borderRadius: '16px',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
          display: 'flex',
          alignItems: 'center',
          gap: '20px',
          transition: 'transform 0.3s ease, box-shadow 0.3s ease'
        }}
        onMouseOver={(e) => {
          e.target.style.transform = 'translateY(-4px)';
          e.target.style.boxShadow = '0 12px 25px rgba(0, 0, 0, 0.15)';
        }}
        onMouseOut={(e) => {
          e.target.style.transform = 'translateY(0)';
          e.target.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)';
        }}
        >
          <div style={{
            width: '60px',
            height: '60px',
            borderRadius: '12px',
            background: 'linear-gradient(135deg, #f59e0b, #d97706)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '1.5rem',
            color: 'white'
          }}>
            <FiCalendar />
          </div>
          <div>
            <h3 style={{
              fontSize: '2rem',
              fontWeight: '700',
              color: '#1e293b',
              margin: '0 0 4px 0'
            }}>
              {stats.recent}
            </h3>
            <p style={{
              fontSize: '0.95rem',
              color: '#64748b',
              margin: '0'
            }}>
              Recent (7 days)
            </p>
          </div>
        </div>
      </div>

      {/* Controls Bar */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '20px',
        marginBottom: '25px',
        flexWrap: 'wrap'
      }}>
        <div style={{
          position: 'relative',
          flex: '1',
          minWidth: '300px'
        }}>
          <FiSearch style={{
            position: 'absolute',
            left: '15px',
            top: '50%',
            transform: 'translateY(-50%)',
            color: '#94a3b8',
            fontSize: '1.1rem'
          }} />
          <input
            type="text"
            placeholder="Search locations by name or ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: '12px 20px 12px 45px',
              border: '2px solid #e2e8f0',
              borderRadius: '12px',
              fontSize: '1rem',
              background: 'white',
              transition: 'all 0.3s ease'
            }}
            onFocus={(e) => {
              e.target.style.borderColor = '#667eea';
              e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = '#e2e8f0';
              e.target.style.boxShadow = 'none';
            }}
          />
        </div>
        
        <div style={{
          display: 'flex',
          gap: '12px'
        }}>
          <select 
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            style={{
              padding: '12px 16px',
              border: '2px solid #e2e8f0',
              borderRadius: '12px',
              fontSize: '1rem',
              background: 'white',
              cursor: 'pointer',
              minWidth: '140px'
            }}
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
          
          <button 
            onClick={() => {
              setSearchTerm('');
              setFilterStatus('all');
            }}
            style={{
              padding: '12px 16px',
              border: '2px solid #e2e8f0',
              borderRadius: '12px',
              fontSize: '1rem',
              background: 'white',
              cursor: 'pointer',
              color: '#64748b',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.3s ease'
            }}
            onMouseOver={(e) => {
              e.target.style.background = '#f8fafc';
              e.target.style.color = '#475569';
            }}
            onMouseOut={(e) => {
              e.target.style.background = 'white';
              e.target.style.color = '#64748b';
            }}
          >
            <FiX /> Clear Filters
          </button>
        </div>
      </div>

      {/* Data Table */}
      <div style={{
        background: 'white',
        borderRadius: '16px',
        overflow: 'hidden',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
      }}>
        {loading ? (
          <div style={{
            textAlign: 'center',
            padding: '60px 40px'
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              border: '4px solid #f1f5f9',
              borderTop: '4px solid #667eea',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 20px'
            }}></div>
            <p style={{
              color: '#64748b',
              fontSize: '1rem',
              margin: '0'
            }}>Loading nearby locations...</p>
          </div>
        ) : sortedLocations.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '60px 40px',
            color: '#64748b'
          }}>
            <FiMapPin style={{
              fontSize: '4rem',
              color: '#cbd5e1',
              marginBottom: '20px'
            }} />
            <h3 style={{
              fontSize: '1.5rem',
              color: '#475569',
              margin: '0 0 12px 0'
            }}>No nearby locations found</h3>
            <p style={{
              fontSize: '1rem',
              margin: '0 0 25px 0'
            }}>Try adjusting your search or filter criteria</p>
            <button 
              onClick={() => setShowModal(true)}
              style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                border: 'none',
                padding: '12px 24px',
                borderRadius: '12px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                margin: '0 auto'
              }}
            >
              <FiPlus /> Add Your First Location
            </button>
          </div>
        ) : (
          <table style={{
            width: '100%',
            borderCollapse: 'collapse'
          }}>
            <thead>
              <tr>
                <th style={{
                  background: '#f8fafc',
                  padding: '16px 20px',
                  textAlign: 'left',
                  fontWeight: '600',
                  color: '#475569',
                  borderBottom: '2px solid #e2e8f0',
                  fontSize: '0.9rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  cursor: 'pointer'
                }}
                onClick={() => handleSort('nearby_location_name')}
                >
                  <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    Nearby Location Name
                    {sortConfig.key === 'nearby_location_name' && (
                      sortConfig.direction === 'asc' ? <FiArrowUp /> : <FiArrowDown />
                    )}
                  </span>
                </th>
                <th style={{
                  background: '#f8fafc',
                  padding: '16px 20px',
                  textAlign: 'left',
                  fontWeight: '600',
                  color: '#475569',
                  borderBottom: '2px solid #e2e8f0',
                  fontSize: '0.9rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  cursor: 'pointer'
                }}
                onClick={() => handleSort('status')}
                >
                  <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    Status
                    {sortConfig.key === 'status' && (
                      sortConfig.direction === 'asc' ? <FiArrowUp /> : <FiArrowDown />
                    )}
                  </span>
                </th>
                <th style={{
                  background: '#f8fafc',
                  padding: '16px 20px',
                  textAlign: 'left',
                  fontWeight: '600',
                  color: '#475569',
                  borderBottom: '2px solid #e2e8f0',
                  fontSize: '0.9rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  cursor: 'pointer'
                }}
                onClick={() => handleSort('cr_date')}
                >
                  <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    Created Date
                    {sortConfig.key === 'cr_date' && (
                      sortConfig.direction === 'asc' ? <FiArrowUp /> : <FiArrowDown />
                    )}
                  </span>
                </th>
                <th style={{
                  background: '#f8fafc',
                  padding: '16px 20px',
                  textAlign: 'left',
                  fontWeight: '600',
                  color: '#475569',
                  borderBottom: '2px solid #e2e8f0',
                  fontSize: '0.9rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedLocations.map(location => {
                const statusConfig = getStatusConfig(location.status);
                return (
                  <tr key={location.nid} style={{
                    transition: 'background 0.3s ease'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.background = '#f8fafc';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.background = 'white';
                  }}
                  >
                    <td style={{ padding: '20px', borderBottom: '1px solid #f1f5f9', verticalAlign: 'top' }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px'
                      }}>
                        {getLocationIcon(location.nearby_location_name)}
                        <div style={{ flex: '1' }}>
                          <h4 style={{
                            fontWeight: '600',
                            color: '#1e293b',
                            margin: '0 0 4px 0',
                            fontSize: '1rem',
                            cursor: 'pointer'
                          }}
                          onClick={() => handleView(location)}
                          >
                            {location.nearby_location_name}
                          </h4>
                          <p style={{
                            color: '#94a3b8',
                            fontSize: '0.875rem',
                            margin: '0',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}>
                            <FiCalendar style={{ fontSize: '0.8rem' }} />
                            Updated: {formatTime(location.update_time || location.cr_date)}
                          </p>
                        </div>
                      </div>
                    </td>
                    
                    <td style={{ padding: '20px', borderBottom: '1px solid #f1f5f9', verticalAlign: 'top' }}>
                      <div style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '6px 12px',
                        borderRadius: '20px',
                        fontSize: '0.8rem',
                        fontWeight: '600',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        background: statusConfig.background,
                        color: statusConfig.color,
                        border: `1px solid ${statusConfig.border}`
                      }}>
                        {statusConfig.icon}
                        {statusConfig.text}
                      </div>
                    </td>
                    
                    <td style={{ padding: '20px', borderBottom: '1px solid #f1f5f9', verticalAlign: 'top' }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        color: '#64748b',
                        fontSize: '0.875rem'
                      }}>
                        <FiCalendar style={{ fontSize: '0.9rem' }} />
                        {formatDate(location.cr_date)}
                      </div>
                    </td>
                    
                    <td style={{ padding: '20px', borderBottom: '1px solid #f1f5f9', verticalAlign: 'top' }}>
                      <div style={{
                        display: 'flex',
                        gap: '8px'
                      }}>
                        <button
                          onClick={() => handleView(location)}
                          style={{
                            background: '#3b82f6',
                            color: 'white',
                            border: 'none',
                            padding: '8px 12px',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontSize: '0.8rem',
                            fontWeight: '600',
                            transition: 'all 0.3s ease',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '5px'
                          }}
                          title="View Details"
                        >
                          <FiEye />
                        </button>
                        <button
                          onClick={() => toggleStatus(location.nid, location.status)}
                          style={{
                            background: location.status === 'active' ? '#f59e0b' : '#10b981',
                            color: 'white',
                            border: 'none',
                            padding: '8px 12px',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontSize: '0.8rem',
                            fontWeight: '600',
                            transition: 'all 0.3s ease',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '5px'
                          }}
                          title={location.status === 'active' ? 'Deactivate' : 'Activate'}
                        >
                          {location.status === 'active' ? <FiEyeOff /> : <FiCheck />}
                        </button>
                        <button
                          onClick={() => handleEdit(location)}
                          style={{
                            background: '#f1f5f9',
                            color: '#475569',
                            border: '1px solid #e2e8f0',
                            padding: '8px 12px',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontSize: '0.8rem',
                            fontWeight: '600',
                            transition: 'all 0.3s ease',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '5px'
                          }}
                          title="Edit"
                        >
                          <FiEdit />
                        </button>
                        <button
                          onClick={() => handleDelete(location.nid)}
                          style={{
                            background: '#ef4444',
                            color: 'white',
                            border: 'none',
                            padding: '8px 12px',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontSize: '0.8rem',
                            fontWeight: '600',
                            transition: 'all 0.3s ease',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '5px'
                          }}
                          title="Delete"
                        >
                          <FiTrash2 />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div style={{
          position: 'fixed',
          top: '0',
          left: '0',
          right: '0',
          bottom: '0',
          background: 'rgba(0, 0, 0, 0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: '1000',
          padding: '20px',
          backdropFilter: 'blur(5px)'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '20px',
            width: '100%',
            maxWidth: '500px',
            maxHeight: '90vh',
            overflow: 'hidden',
            boxShadow: '0 25px 50px rgba(0, 0, 0, 0.25)',
            animation: 'modalSlideIn 0.3s ease'
          }}>
            <div style={{
              padding: '25px 30px',
              borderBottom: '1px solid #e2e8f0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white'
            }}>
              <h2 style={{
                margin: '0',
                fontSize: '1.5rem',
                fontWeight: '600'
              }}>
                {editingLocation ? 'Edit Nearby Location' : 'Add New Nearby Location'}
              </h2>
              <button 
                onClick={() => {
                  setShowModal(false);
                  resetForm();
                }}
                style={{
                  background: 'rgba(255, 255, 255, 0.2)',
                  border: 'none',
                  fontSize: '1.5rem',
                  color: 'white',
                  cursor: 'pointer',
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'background 0.3s ease'
                }}
                onMouseOver={(e) => {
                  e.target.style.background = 'rgba(255, 255, 255, 0.3)';
                }}
                onMouseOut={(e) => {
                  e.target.style.background = 'rgba(255, 255, 255, 0.2)';
                }}
              >
                &times;
              </button>
            </div>

            <form onSubmit={handleSubmit} style={{ padding: '30px' }}>
              <div style={{ marginBottom: '25px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: '600',
                  color: '#374151',
                  fontSize: '0.95rem'
                }}>
                  Nearby Location Name *
                </label>
                <input
                  type="text"
                  name="nearby_location_name"
                  value={formData.nearby_location_name}
                  onChange={handleInputChange}
                  placeholder="Enter nearby location name (e.g., Behind Dr. Agarwal's Eye Hospital)"
                  required
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    transition: 'all 0.3s ease'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#667eea';
                    e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e2e8f0';
                    e.target.style.boxShadow = 'none';
                  }}
                />
                <p style={{
                  margin: '8px 0 0 0',
                  color: '#94a3b8',
                  fontSize: '0.85rem'
                }}>
                  Examples: "Behind Dr. Agarwal's Eye Hospital", "Near Metro Station", "Opposite Shopping Mall"
                </p>
              </div>

              <div style={{ marginBottom: '25px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: '600',
                  color: '#374151',
                  fontSize: '0.95rem'
                }}>
                  Status
                </label>
                <div style={{
                  display: 'flex',
                  gap: '15px'
                }}>
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    cursor: 'pointer',
                    padding: '10px 15px',
                    borderRadius: '8px',
                    border: `2px solid ${formData.status === 'active' ? '#10b981' : '#e2e8f0'}`,
                    background: formData.status === 'active' ? '#f0fdf4' : 'white',
                    flex: '1',
                    transition: 'all 0.3s ease'
                  }}>
                    <input
                      type="radio"
                      name="status"
                      value="active"
                      checked={formData.status === 'active'}
                      onChange={handleInputChange}
                      style={{ transform: 'scale(1.2)' }}
                    />
                    <div>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        color: '#065f46',
                        fontWeight: '600'
                      }}>
                        <FiCheck /> Active
                      </div>
                      <p style={{
                        margin: '4px 0 0 0',
                        color: '#94a3b8',
                        fontSize: '0.85rem'
                      }}>
                        Location will be available
                      </p>
                    </div>
                  </label>
                  
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    cursor: 'pointer',
                    padding: '10px 15px',
                    borderRadius: '8px',
                    border: `2px solid ${formData.status === 'inactive' ? '#ef4444' : '#e2e8f0'}`,
                    background: formData.status === 'inactive' ? '#fef2f2' : 'white',
                    flex: '1',
                    transition: 'all 0.3s ease'
                  }}>
                    <input
                      type="radio"
                      name="status"
                      value="inactive"
                      checked={formData.status === 'inactive'}
                      onChange={handleInputChange}
                      style={{ transform: 'scale(1.2)' }}
                    />
                    <div>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        color: '#dc2626',
                        fontWeight: '600'
                      }}>
                        <FiX /> Inactive
                      </div>
                      <p style={{
                        margin: '4px 0 0 0',
                        color: '#94a3b8',
                        fontSize: '0.85rem'
                      }}>
                        Location will be hidden
                      </p>
                    </div>
                  </label>
                </div>
              </div>

              <div style={{
                paddingTop: '25px',
                borderTop: '1px solid #e2e8f0',
                display: 'flex',
                gap: '15px',
                justifyContent: 'flex-end'
              }}>
                <button 
                  type="button" 
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  style={{
                    background: 'transparent',
                    color: '#64748b',
                    border: '2px solid #e2e8f0',
                    padding: '12px 25px',
                    borderRadius: '10px',
                    cursor: 'pointer',
                    fontSize: '1rem',
                    fontWeight: '500',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseOver={(e) => {
                    e.target.style.background = '#f8fafc';
                    e.target.style.color = '#475569';
                  }}
                  onMouseOut={(e) => {
                    e.target.style.background = 'transparent';
                    e.target.style.color = '#64748b';
                  }}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  disabled={processing}
                  style={{
                    background: processing ? '#94a3b8' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    border: 'none',
                    padding: '12px 30px',
                    borderRadius: '10px',
                    cursor: processing ? 'not-allowed' : 'pointer',
                    fontSize: '1rem',
                    fontWeight: '600',
                    transition: 'all 0.3s ease',
                    boxShadow: processing ? 'none' : '0 4px 15px rgba(102, 126, 234, 0.3)',
                    opacity: processing ? 0.7 : 1
                  }}
                  onMouseOver={(e) => {
                    if (!processing) {
                      e.target.style.transform = 'translateY(-2px)';
                      e.target.style.boxShadow = '0 8px 25px rgba(102, 126, 234, 0.4)';
                    }
                  }}
                  onMouseOut={(e) => {
                    if (!processing) {
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = '0 4px 15px rgba(102, 126, 234, 0.3)';
                    }
                  }}
                >
                  {processing ? 'Processing...' : (editingLocation ? 'Update Location' : 'Add Location')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Details Modal */}
      {showViewModal && viewingLocation && (
        <div style={{
          position: 'fixed',
          top: '0',
          left: '0',
          right: '0',
          bottom: '0',
          background: 'rgba(0, 0, 0, 0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: '1000',
          padding: '20px',
          backdropFilter: 'blur(5px)'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '20px',
            width: '100%',
            maxWidth: '500px',
            maxHeight: '90vh',
            overflow: 'hidden',
            boxShadow: '0 25px 50px rgba(0, 0, 0, 0.25)',
            animation: 'modalSlideIn 0.3s ease'
          }}>
            <div style={{
              padding: '25px 30px',
              borderBottom: '1px solid #e2e8f0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white'
            }}>
              <h2 style={{
                margin: '0',
                fontSize: '1.5rem',
                fontWeight: '600'
              }}>
                Nearby Location Details
              </h2>
              <button 
                onClick={() => setShowViewModal(false)}
                style={{
                  background: 'rgba(255, 255, 255, 0.2)',
                  border: 'none',
                  fontSize: '1.5rem',
                  color: 'white',
                  cursor: 'pointer',
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'background 0.3s ease'
                }}
                onMouseOver={(e) => {
                  e.target.style.background = 'rgba(255, 255, 255, 0.3)';
                }}
                onMouseOut={(e) => {
                  e.target.style.background = 'rgba(255, 255, 255, 0.2)';
                }}
              >
                &times;
              </button>
            </div>

            <div style={{ padding: '30px' }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '20px',
                marginBottom: '25px',
                paddingBottom: '25px',
                borderBottom: '1px solid #e2e8f0'
              }}>
                <div style={{
                  width: '70px',
                  height: '70px',
                  borderRadius: '16px',
                  background: 'linear-gradient(135deg, #667eea, #764ba2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '2rem',
                  color: 'white'
                }}>
                  {getLocationIcon(viewingLocation.nearby_location_name)}
                </div>
                <div>
                  <div style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '6px 12px',
                    borderRadius: '20px',
                    fontSize: '0.8rem',
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    background: getStatusConfig(viewingLocation.status).background,
                    color: getStatusConfig(viewingLocation.status).color,
                    border: `1px solid ${getStatusConfig(viewingLocation.status).border}`,
                    marginBottom: '10px'
                  }}>
                    {getStatusConfig(viewingLocation.status).icon}
                    {getStatusConfig(viewingLocation.status).text}
                  </div>
                  <h3 style={{
                    fontSize: '1.5rem',
                    fontWeight: '700',
                    color: '#1e293b',
                    margin: '0'
                  }}>
                    {viewingLocation.nearby_location_name}
                  </h3>
                </div>
              </div>

              <div style={{
                background: '#f8fafc',
                borderRadius: '12px',
                padding: '20px',
                marginBottom: '25px'
              }}>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  gap: '20px'
                }}>
                  <div>
                    <h4 style={{
                      fontSize: '0.8rem',
                      fontWeight: '600',
                      color: '#64748b',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      margin: '0 0 8px 0'
                    }}>
                      Location ID
                    </h4>
                    <p style={{
                      fontSize: '1.2rem',
                      fontWeight: '600',
                      color: '#1e293b',
                      margin: '0',
                      fontFamily: "'Roboto Mono', monospace"
                    }}>
                      #{viewingLocation.nid}
                    </p>
                  </div>
                  
                  <div>
                    <h4 style={{
                      fontSize: '0.8rem',
                      fontWeight: '600',
                      color: '#64748b',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      margin: '0 0 8px 0'
                    }}>
                      Created Date
                    </h4>
                    <p style={{
                      fontSize: '1rem',
                      fontWeight: '500',
                      color: '#1e293b',
                      margin: '0'
                    }}>
                      {formatDate(viewingLocation.cr_date)}
                    </p>
                  </div>
                  
                  <div>
                    <h4 style={{
                      fontSize: '0.8rem',
                      fontWeight: '600',
                      color: '#64748b',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      margin: '0 0 8px 0'
                    }}>
                      Last Updated
                    </h4>
                    <p style={{
                      fontSize: '1rem',
                      fontWeight: '500',
                      color: '#1e293b',
                      margin: '0'
                    }}>
                      {formatDate(viewingLocation.update_time || viewingLocation.cr_date)}
                    </p>
                  </div>
                  
                  <div>
                    <h4 style={{
                      fontSize: '0.8rem',
                      fontWeight: '600',
                      color: '#64748b',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      margin: '0 0 8px 0'
                    }}>
                      Time Created
                    </h4>
                    <p style={{
                      fontSize: '1rem',
                      fontWeight: '500',
                      color: '#1e293b',
                      margin: '0'
                    }}>
                      {formatTime(viewingLocation.cr_date)}
                    </p>
                  </div>
                </div>
              </div>

              <div style={{
                display: 'flex',
                gap: '12px',
                justifyContent: 'center'
              }}>
                <button
                  onClick={() => {
                    setShowViewModal(false);
                    handleEdit(viewingLocation);
                  }}
                  style={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    border: 'none',
                    padding: '12px 24px',
                    borderRadius: '10px',
                    cursor: 'pointer',
                    fontSize: '1rem',
                    fontWeight: '600',
                    transition: 'all 0.3s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    flex: '1'
                  }}
                >
                  <FiEdit /> Edit Location
                </button>
                
                <button
                  onClick={() => setShowViewModal(false)}
                  style={{
                    background: 'transparent',
                    color: '#64748b',
                    border: '2px solid #e2e8f0',
                    padding: '12px 24px',
                    borderRadius: '10px',
                    cursor: 'pointer',
                    fontSize: '1rem',
                    fontWeight: '500',
                    transition: 'all 0.3s ease',
                    flex: '1'
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes modalSlideIn {
          from {
            opacity: 0;
            transform: translateY(-50px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
      `}</style>
    </div>
  );
};

export default NearbyLocationsManagement;